//frontend\src\components\CashExtras.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanknotesIcon,
  ChevronDownIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/solid';

import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import CashExtrasTotalByMatrizador from './CashExtrasTotalByMatrizador.jsx';

/**
 * CashExtras.jsx — FINAL
 *
 * Invariantes:
 * - SOLO extras (NO ítems reales).
 * - Visible SOLO para NOTARIO.
 * - Selección por DÍA (documento) como evidencia/selector.
 * - Desglose detallado por TRÁMITE y totales por MATRIZADOR.
 * - Marcar PAGADO (estado=cancelado) y desactivar botón al registrar.
 *
 * Backend (observado):
 * - GET  /api/audit/document-days/
 * - GET  /api/audit/configure-commission/   (solo notario)
 * - GET  /api/audit/list/?fecha_documento=YYYY-MM-DD
 * - GET  /api/audit/cash-extras/?fecha_documento=YYYY-MM-DD   (upsert ledger)
 * - PATCH /api/audit/cash-extras/  { id, estado, notas }
 * - PATCH /api/audit/cash-extras/  { ids, estado, notas }     (bulk, pestaña total)
 */

const isISODate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

const normalizeText = (text) => {
  if (typeof text !== 'string') return '';
  // NFD + remove diacritics + uppercase
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ');
};

const toMoney = (n) => {
  const num = Number(n);
  if (Number.isNaN(num) || !Number.isFinite(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
};

const toNumber2 = (n) => {
  const num = Number(n);
  if (Number.isNaN(num) || !Number.isFinite(num)) return 0;
  // Evitar errores de coma flotante
  return Math.round(num * 100) / 100;
};

const getUnitForDate = (commission, fechaISO) => {
  // commission.values: [{ value, start_date, end_date }]
  if (!commission || !isISODate(fechaISO)) return 0;
  const values = Array.isArray(commission.values) ? commission.values : [];
  if (values.length === 0) return 0;

  const target = fechaISO;
  // Elegir el valor con start_date más reciente que cubra la fecha
  const candidates = values
    .filter((v) => {
      if (!v?.start_date) return false;
      const start = String(v.start_date);
      const end = v.end_date ? String(v.end_date) : null;
      if (!isISODate(start)) return false;
      if (start > target) return false;
      if (end && isISODate(end) && end < target) return false;
      return true;
    })
    .sort((a, b) => String(b.start_date).localeCompare(String(a.start_date)));

  const pick = candidates[0] || values[0];
  return toNumber2(pick?.value || 0);
};

const CashExtras = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };

  const [availableDays, setAvailableDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');

  const [commissions, setCommissions] = useState([]);

  const [ledger, setLedger] = useState([]); // CashExtraLedger entries for selected day
  const [tramites, setTramites] = useState([]); // Tramites for selected day

  const [loadingDays, setLoadingDays] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [submittingLedgerId, setSubmittingLedgerId] = useState(null);
  const [activeTab, setActiveTab] = useState('daily');
  const [totalRefreshToken, setTotalRefreshToken] = useState(0);
  const [loadingTotal, setLoadingTotal] = useState(false);

  const [matrizadorFilter, setMatrizadorFilter] = useState('');

  // UX funcional: por defecto, lista de matrizadores colapsada.
  // Solo al hacer click sobre el matrizador se abre el detalle de trámites.
  const [expandedMatrizador, setExpandedMatrizador] = useState('');

  /* =========================
     Seguridad / permisos
     ========================= */
  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a Valores Extra.');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  /* =========================
     Cargar días disponibles
     ========================= */
  const fetchDays = useCallback(async () => {
    setLoadingDays(true);
    try {
      const res = await api.get('/api/audit/document-days/', { params: { page: 1, page_size: 200 } });
      const days = Array.isArray(res.data?.days) ? res.data.days : [];
      setAvailableDays(days);
      // Selector usable: si no hay día seleccionado aún, sugerir el más reciente
      if (!selectedDay && days.length > 0) {
        setSelectedDay(String(days[0]));
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar los días disponibles.');
      setAvailableDays([]);
    } finally {
      setLoadingDays(false);
    }
  }, [selectedDay]);

  /* =========================
     Cargar configuración de comisiones
     ========================= */
  const fetchCommissions = useCallback(async () => {
    try {
      const res = await api.get('/api/audit/configure-commission/');
      const list = Array.isArray(res.data?.commissions) ? res.data.commissions : [];
      setCommissions(list);
    } catch (err) {
      console.error(err);
      toast.error('No se pudo cargar la configuración de comisiones.');
      setCommissions([]);
    }
  }, []);

  useEffect(() => {
    if (!loading && user?.is_notario) {
      fetchDays();
      fetchCommissions();
    }
  }, [loading, user, fetchDays, fetchCommissions]);

  /* =========================
     Fetch tramites (paginado) para el día
     ========================= */
  const fetchAllTramitesForDay = useCallback(async (fechaISO) => {
    const pageSize = 200;
    let page = 1;
    let totalPages = 1;
    let all = [];

    // Salvaguarda: 50 páginas máximas
    while (page <= totalPages && page <= 50) {
      const res = await api.get('/api/audit/list/', {
        params: {
          fecha_documento: fechaISO,
          page,
          page_size: pageSize,
        },
      });
      const data = res.data || {};
      const batch = Array.isArray(data?.tramites) ? data.tramites : [];
      all = all.concat(batch);
      totalPages = Number(data?.total_pages || 1);
      page += 1;
    }

    return all;
  }, []);

  /* =========================
     Cargar ledger + tramites para el día
     ========================= */
  const fetchForDay = useCallback(async () => {
    if (!isISODate(selectedDay)) {
      setLedger([]);
      setTramites([]);
      return;
    }

    setLoadingData(true);
    try {
      // 1) Ledger (upsert server-side)
      const resLedger = await api.get('/api/audit/cash-extras/', {
        params: { fecha_documento: selectedDay },
      });
      const ledgerList = Array.isArray(resLedger.data)
        ? resLedger.data
        : Array.isArray(resLedger.data?.results)
          ? resLedger.data.results
          : [];

      // 2) Tramites para desglose detallado
      const allTramites = await fetchAllTramitesForDay(selectedDay);

      setLedger(Array.isArray(ledgerList) ? ledgerList : []);
      setTramites(Array.isArray(allTramites) ? allTramites : []);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar datos de extras para el día seleccionado.');
      setLedger([]);
      setTramites([]);
    } finally {
      setLoadingData(false);
    }
  }, [selectedDay, fetchAllTramitesForDay]);

  useEffect(() => {
    if (!loading && user?.is_notario && isISODate(selectedDay)) {
      fetchForDay();
    }
  }, [loading, user, selectedDay, fetchForDay]);

  /* =========================
     Comisiones normalizadas y unidades por día
     ========================= */
  const commissionTypes = useMemo(() => {
    const list = Array.isArray(commissions) ? commissions : [];
    return list
      .map((c) => ({
        id: c.id,
        name: String(c.name || '').trim(),
        name_norm: normalizeText(String(c.name || '')),
        values: Array.isArray(c.values) ? c.values : [],
      }))
      .filter((c) => Boolean(c.name))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [commissions]);

  const unitByType = useMemo(() => {
    const out = {};
    commissionTypes.forEach((ct) => {
      out[ct.name] = getUnitForDate(ct, selectedDay);
    });
    return out;
  }, [commissionTypes, selectedDay]);

  /* =========================
     Ledger map por matrizador (estado pagado)
     ========================= */
  const ledgerByMatrizador = useMemo(() => {
    const map = {};
    (Array.isArray(ledger) ? ledger : []).forEach((l) => {
      const key = normalizeText(String(l?.usuario_matrizador || ''));
      if (!key) return;
      const current = map[key];
      if (current?.estado === 'cancelado' && l?.estado !== 'cancelado') return;
      map[key] = l;
    });
    return map;
  }, [ledger]);

  /* =========================
     Cálculo de extras por trámite (frontend) — SOLO PARA ESTA VISTA
     ========================= */
  const computedTramites = useMemo(() => {
    const tList = Array.isArray(tramites) ? tramites : [];
    if (tList.length === 0 || commissionTypes.length === 0) return [];

    // Regla solicitada: se calcula por ITEM; cada trámite debe mapear a **un solo** tipo de extra.
    // (Si por inconsistencia de datos llegan 2+ tipos en el mismo trámite, NO se debe cobrar doble.)
    const typeByNorm = new Map();
    commissionTypes.forEach((ct) => {
      if (ct?.name_norm) typeByNorm.set(ct.name_norm, ct.name);
    });

    return tList.map((t) => {
      const items = Array.isArray(t?.items) ? t.items : [];

      const descNorm = items.map((it) => normalizeText(String(it?.descripcion || '')));
      const actoNorm = normalizeText(String(t?.acto || t?.objeto_escritura || t?.objeto || t?.objeto_acto || ''));

      // Determinar 1 tipo (máximo) por trámite
      let assignedTypeName = null;
      for (let i = 0; i < descNorm.length; i += 1) {
        const n = descNorm[i];
        const matched = typeByNorm.get(n);
        if (matched) {
          assignedTypeName = matched;
          break;
        }
      }

      // Fallback: si no hay items mapeables, intentar mapear por acto solo si es un match ÚNICO.
      if (!assignedTypeName && actoNorm) {
        const candidates = [];
        commissionTypes.forEach((ct) => {
          if (ct?.name_norm && actoNorm.includes(ct.name_norm)) candidates.push(ct.name);
        });
        if (candidates.length === 1) assignedTypeName = candidates[0];
      }

      const counts = {};
      const totals = {};
      let totalExtra = 0;

      commissionTypes.forEach((ct) => {
        const count = assignedTypeName && ct.name === assignedTypeName ? 1 : 0;
        const unit = unitByType[ct.name] || 0;
        const total = toNumber2(count * unit);
        counts[ct.name] = count;
        totals[ct.name] = total;
        totalExtra += total;
      });

      totalExtra = toNumber2(totalExtra);

      return {
        tramite: t,
        counts,
        totals,
        totalExtra,
      };
    });
  }, [tramites, commissionTypes, unitByType]);

  /* =========================
     Agrupar por matrizador + filtros
     ========================= */
  const groupedByMatrizador = useMemo(() => {
    const groups = {};

    computedTramites.forEach((row) => {
      // Solo considerar trámites con extras > 0
      if (!row || row.totalExtra <= 0) return;

      // OJO: usuario_matrizador en ledger se guarda normalizado (uppercase + sin tildes)
      // Por eso la key debe ser normalizada para poder cruzar FE (tramites) vs BE (ledger).
      const label = String(row?.tramite?.usuario_matrizador || 'SIN MATRIZADOR').trim() || 'SIN MATRIZADOR';
      const key = normalizeText(label);

      if (!groups[key]) {
        const initTotalsByType = {};
        commissionTypes.forEach((ct) => {
          initTotalsByType[ct.name] = 0;
        });
        groups[key] = {
          key,
          usuario_matrizador: label, // etiqueta para UI
          rows: [],
          totalsByType: initTotalsByType,
          totalExtra: 0,
        };
      }

      groups[key].rows.push(row);
      commissionTypes.forEach((ct) => {
        groups[key].totalsByType[ct.name] = toNumber2(
          (groups[key].totalsByType[ct.name] || 0) + (row.totals?.[ct.name] || 0)
        );
      });
      groups[key].totalExtra = toNumber2((groups[key].totalExtra || 0) + (row.totalExtra || 0));
    });

    let list = Object.values(groups);

    // Filtrar por matrizador (dropdown/teclado)
    const f = normalizeText(matrizadorFilter);
    if (f) {
      list = list.filter(
        (g) => g?.key === f || String(g?.key || '').includes(f) || normalizeText(g?.usuario_matrizador || '').includes(f)
      );
    }

    // Orden alfabético por etiqueta visible
    list.sort((a, b) => String(a.usuario_matrizador).localeCompare(String(b.usuario_matrizador), 'es'));

    return list;
  }, [computedTramites, commissionTypes, matrizadorFilter]);

  // Si el filtro elimina el matrizador expandido, colapsar.
  useEffect(() => {
    if (!expandedMatrizador) return;
    const exists = groupedByMatrizador.some((g) => g?.key === expandedMatrizador);
    if (!exists) setExpandedMatrizador('');
  }, [expandedMatrizador, groupedByMatrizador]);

  const matrizadorOptions = useMemo(() => {
    // key normalizada -> etiqueta original
    const map = new Map();
    computedTramites.forEach((row) => {
      if (!row || row.totalExtra <= 0) return;
      const label = String(row?.tramite?.usuario_matrizador || 'SIN MATRIZADOR').trim() || 'SIN MATRIZADOR';
      const key = normalizeText(label);
      if (!map.has(key)) map.set(key, label);
    });

    return Array.from(map.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'es'));
  }, [computedTramites]);

  const totalDayExtras = useMemo(() => {
    return groupedByMatrizador.reduce((acc, g) => acc + Number(g?.totalExtra || 0), 0);
  }, [groupedByMatrizador]);

  /* =========================
     Acción: marcar pagado (cancelado)
     ========================= */
  const markPaid = async (ledgerId) => {
    if (!ledgerId) return;
    setSubmittingLedgerId(ledgerId);
    try {
      await api.patch('/api/audit/cash-extras/', { id: ledgerId, estado: 'cancelado' });
      toast.success('Marcado como pagado.');
      await fetchForDay();
    } catch (err) {
      const msg = err?.response?.data?.error || 'No se pudo marcar como pagado.';
      toast.error(msg);
    } finally {
      setSubmittingLedgerId(null);
    }
  };

  /* =========================
     UI
     ========================= */
  const daySuggestions = useMemo(() => {
    return (Array.isArray(availableDays) ? availableDays : []).slice(0, 30);
  }, [availableDays]);

  const selectedDayIsAvailable = useMemo(() => {
    if (!selectedDay) return false;
    return (Array.isArray(availableDays) ? availableDays : []).includes(selectedDay);
  }, [availableDays, selectedDay]);

  const refreshActiveTab = useCallback(() => {
    if (activeTab === 'total') {
      setTotalRefreshToken((prev) => prev + 1);
      return;
    }
    fetchForDay();
  }, [activeTab, fetchForDay]);

  const refreshDisabled = activeTab === 'total' ? loadingTotal : loadingData || !isISODate(selectedDay);
  const refreshSpinning = activeTab === 'total' ? loadingTotal : loadingData;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />

      <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Notaría 4 Cantón Otavalo" className="w-12 h-auto sm:w-14" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Valor  Extras (Efectivo)</h1>
            <p className="text-sm text-gray-300">
              Detalle por trámite y totales por matrizador — solo NOTARIO
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <motion.button
            onClick={refreshActiveTab}
            disabled={refreshDisabled}
            className="bg-notaria-blue-2 text-white py-2 px-4 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all disabled:opacity-50"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            aria-label="Refrescar"
            title="Refrescar"
          >
            <ArrowPathIcon className={`w-5 h-5 ${refreshSpinning ? 'animate-spin' : ''}`} />
          </motion.button>

          <Link
            to="/dashboard/notario"
            className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all"
          >
            Volver
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full space-y-6">
        <div className="rounded-2xl border border-white/10 bg-notaria-blue-1/80 p-2 shadow-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="tablist" aria-label="Tipos de cobro de valores extra">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'daily'}
              onClick={() => setActiveTab('daily')}
              className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-extrabold transition-all ${
                activeTab === 'daily'
                  ? 'bg-notaria-yellow text-notaria-blue-1 shadow-lg'
                  : 'bg-notaria-blue-2/40 text-gray-200 hover:bg-white/10'
              }`}
            >
              <CalendarDaysIcon className="h-5 w-5" />
              Cobro por día
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'total'}
              onClick={() => setActiveTab('total')}
              className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-extrabold transition-all ${
                activeTab === 'total'
                  ? 'bg-notaria-yellow text-notaria-blue-1 shadow-lg'
                  : 'bg-notaria-blue-2/40 text-gray-200 hover:bg-white/10'
              }`}
            >
              <ClipboardDocumentCheckIcon className="h-5 w-5" />
              Cobro total por matrizador
            </button>
          </div>
        </div>

        {activeTab === 'daily' ? (
          <>
        {/* Filtros */}
        <div className="bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-2xl shadow-lg">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
              <div>
                <label className="block text-sm font-semibold mb-1">Día (documento)</label>
                <input
                  list="cash-extras-days"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}
                  placeholder="YYYY-MM-DD"
                  className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                />
                <datalist id="cash-extras-days">
                  {(Array.isArray(availableDays) ? availableDays : []).map((d) => (
                    <option key={d} value={d} />
                  ))}
                </datalist>
                <div className="mt-2 flex flex-wrap gap-2">
                  {daySuggestions.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSelectedDay(d)}
                      className={`text-xs px-2 py-1 rounded-full border transition-all ${
                        d === selectedDay
                          ? 'bg-notaria-yellow text-notaria-blue-1 border-notaria-yellow'
                          : 'bg-notaria-blue-2/40 text-white border-white/10 hover:bg-notaria-yellow hover:text-notaria-blue-1'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                  {loadingDays ? (
                    <span className="text-xs text-gray-300 ml-1">Cargando días…</span>
                  ) : null}
                </div>
                {!selectedDayIsAvailable && selectedDay ? (
                  <p className="text-xs text-notaria-red mt-2">
                    Este día no aparece en los documentos disponibles.
                  </p>
                ) : null}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Filtrar matrizador</label>
                <select
                  value={matrizadorFilter}
                  onChange={(e) => setMatrizadorFilter(e.target.value)}
                  className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                >
                  <option value="">Todos</option>
                  {matrizadorOptions.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
<p className="text-xs text-gray-300 mt-2">
                  Solo se muestran matrizadores con extras &gt; 0.
                </p>
              </div>

              <div className="flex flex-col justify-end">
                <div className="bg-notaria-blue-2/40 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-200 font-semibold">Total Extras del Día</span>
                    <span className="text-lg font-extrabold">{toMoney(totalDayExtras)}</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-300">
                    Calculado automáticamente desde ítems + configuración (solo efectivo).
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido */}
        {loadingData ? (
          <div className="bg-notaria-blue-1 bg-opacity-80 p-6 rounded-2xl shadow-lg">
            <p className="text-gray-200">Cargando / calculando extras…</p>
          </div>
        ) : groupedByMatrizador.length === 0 ? (
          <div className="bg-notaria-blue-1 bg-opacity-80 p-6 rounded-2xl shadow-lg">
            <p className="text-gray-200">
              {selectedDay ? 'No se detectan extras para el día seleccionado.' : 'Selecciona un día para ver extras.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedByMatrizador.map((g) => {
              const l = ledgerByMatrizador[g.key];
              const estado = l?.estado || 'pendiente';
              const isCancelado = estado === 'cancelado';
              const canMarkPaid = Boolean(l?.id) && !isCancelado;
              const isSubmitting = submittingLedgerId === l?.id;

              const isExpanded = expandedMatrizador === g.key;
              const detailsId = `mx-${encodeURIComponent(g.key)}`;

              // Diferencia controlada (auditoría): ledger vs cálculo detalle
              const missingLedger = !l;
              const ledgerTotal = toNumber2(l?.total_extra || 0);
              const calcTotal = toNumber2(g.totalExtra || 0);
              const diff = toNumber2(ledgerTotal - calcTotal);
              const hasDiff = !missingLedger && Math.abs(diff) >= 0.01;

              return (
                <motion.section
                  key={g.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-notaria-blue-1 bg-opacity-80 rounded-2xl shadow-lg overflow-hidden"
                >
                  {/* Header: siempre visible. Click abre/cierra detalle */}
                  <div className="p-3 sm:p-4 bg-notaria-blue-2 bg-opacity-40 border-b border-white/10">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setExpandedMatrizador((prev) => (prev === g.key ? '' : g.key))}
                        aria-expanded={isExpanded}
                        aria-controls={detailsId}
                        className="w-full lg:flex-1 text-left rounded-2xl p-4 hover:bg-notaria-blue-2/40 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-lg sm:text-xl font-extrabold truncate">{g.usuario_matrizador}</div>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-notaria-blue-2/40">
                                <BanknotesIcon className="w-5 h-5 text-notaria-yellow" />
                                <span className="font-semibold">Total:</span>
                                <span className="font-extrabold">{toMoney(g.totalExtra)}</span>
                              </span>

                              <span
                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                                  isCancelado
                                    ? 'border-green-400/30 bg-green-500/10 text-green-200'
                                    : 'border-yellow-300/30 bg-yellow-500/10 text-yellow-200'
                                }`}
                              >
                                {isCancelado ? (
                                  <CheckCircleIcon className="w-5 h-5" />
                                ) : (
                                  <XCircleIcon className="w-5 h-5" />
                                )}
                                <span className="font-semibold">Estado:</span>
                                <span className="font-extrabold">{isCancelado ? 'PAGADO' : 'PENDIENTE'}</span>
                              </span>

                              <span className="text-gray-200">
                                <span className="font-semibold">{g.rows.length}</span> trámites con extras
                              </span>
                            </div>

                            {isCancelado && l?.marcado_por ? (
                              <div className="mt-2 text-xs text-gray-200">
                                Marcado por <span className="font-semibold">{l.marcado_por}</span>{' '}
                                {l.marcado_en ? (
                                  <span className="text-gray-300">({String(l.marcado_en).replace('T', ' ').slice(0, 19)})</span>
                                ) : null}
                              </div>
                            ) : null}

                            {missingLedger ? (
                              <div className="mt-2 text-xs text-notaria-red">
                                No existe LEDGER para este matrizador en esta fecha. No se puede marcar como pagado hasta que el backend genere el
                                registro.
                              </div>
                            ) : hasDiff ? (
                              <div className="mt-2 text-xs text-notaria-red">
                                Diferencia LEDGER ({toMoney(ledgerTotal)}) vs DETALLE ({toMoney(calcTotal)}): {toMoney(diff)}.
                              </div>
                            ) : null}
                          </div>

                          <ChevronDownIcon
                            className={`w-6 h-6 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="mt-3 text-xs text-gray-300">
                          {isExpanded ? 'Ocultar detalle' : 'Ver detalle de trámites'}
                        </div>
                      </button>

                      <div className="flex items-center justify-end lg:justify-start px-2">
                        <motion.button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            markPaid(l?.id);
                          }}
                          disabled={!canMarkPaid || isSubmitting}
                          whileHover={{ scale: canMarkPaid ? 1.03 : 1 }}
                          whileTap={{ scale: canMarkPaid ? 0.97 : 1 }}
                          className={`px-4 py-2 rounded-full font-bold transition-all w-full sm:w-auto ${
                            canMarkPaid
                              ? 'bg-notaria-yellow text-notaria-blue-1 hover:bg-notaria-red hover:text-white'
                              : 'bg-notaria-blue-2 text-gray-300 cursor-not-allowed'
                          }`}
                          title={
                            !l?.id
                              ? 'No existe ledger para este matrizador (sin extras o error de cálculo).'
                              : isCancelado
                                ? 'Ya está pagado.'
                                : 'Marcar como pagado'
                          }
                        >
                          {isSubmitting ? 'Guardando…' : isCancelado ? 'Pagado' : 'Guardar pagado'}
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  {/* Detalle: solo cuando está expandido */}
                  <AnimatePresence initial={false}>
                    {isExpanded ? (
                      <motion.div
                        id={detailsId}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="p-4 sm:p-6"
                      >
                        <div className="overflow-x-auto">
                          <table className="w-full table-fixed text-xs sm:text-sm border-collapse">
                            <thead>
                              <tr className="bg-notaria-blue-2 bg-opacity-60 text-white">
                                <th className="p-2 text-left whitespace-nowrap w-[96px]">No. Temporal</th>
                                <th className="p-2 text-left whitespace-nowrap w-[170px]">No. Comprobante</th>
                                <th className="p-2 text-left whitespace-nowrap w-[130px]">Fecha Documento</th>
                                <th className="p-2 text-left w-[260px]">Objeto / Acto</th>
                                {commissionTypes.map((ct) => (
                                  <th key={ct.name} className="p-2 text-right align-top w-[170px] whitespace-normal break-words">
                                    {ct.name}
                                    <div className="text-[10px] text-gray-200 font-normal">
                                      Unit: {toMoney(unitByType[ct.name] || 0)}
                                    </div>
                                  </th>
                                ))}
                                <th className="p-2 text-right whitespace-nowrap">Total Extra Trámite</th>
                              </tr>
                            </thead>

                            <tbody>
                              {g.rows.map((r) => {
                                const t = r.tramite || {};
                                return (
                                  <tr
                                    key={t.id || `${t.no_temporal}-${t.no_comprobante}`}
                                    className="border-b border-white/10"
                                  >
                                    <td className="p-2 whitespace-nowrap">{t.no_temporal || '-'}</td>
                                    <td className="p-2 whitespace-nowrap">{t.no_comprobante || '-'}</td>
                                    <td className="p-2 whitespace-nowrap">{t.fecha_documento || selectedDay || '-'}</td>
                                    <td className="p-2">
                                      <div className="font-semibold text-white break-words leading-snug">{t.acto || '-'}</div>
                                    </td>

                                    {commissionTypes.map((ct) => {
                                      const total = r.totals?.[ct.name] || 0;
                                      const count = r.counts?.[ct.name] || 0;
                                      return (
                                        <td key={ct.name} className="p-2 text-right whitespace-nowrap">
                                          <div className="font-bold">{toMoney(total)}</div>
                                          <div className="text-[10px] text-gray-300">x{count}</div>
                                        </td>
                                      );
                                    })}

                                    <td className="p-2 text-right whitespace-nowrap font-extrabold">
                                      {toMoney(r.totalExtra || 0)}
                                    </td>
                                  </tr>
                                );
                              })}

                              {/* Totales por matrizador */}
                              <tr className="bg-notaria-blue-2 bg-opacity-40 border-t border-white/10">
                                <td className="p-2 font-bold" colSpan={4}>
                                  Totales ({g.rows.length} trámites)
                                </td>
                                {commissionTypes.map((ct) => (
                                  <td key={ct.name} className="p-2 text-right font-bold whitespace-nowrap">
                                    {toMoney(g.totalsByType?.[ct.name] || 0)}
                                  </td>
                                ))}
                                <td className="p-2 text-right font-extrabold whitespace-nowrap">
                                  {toMoney(g.totalExtra || 0)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </motion.section>
              );
            })}
          </div>
        )}
          </>
        ) : (
          <CashExtrasTotalByMatrizador
            refreshToken={totalRefreshToken}
            onLoadingChange={setLoadingTotal}
          />
        )}
      </main>
    </motion.div>
  );
};

export default CashExtras;
